import type {
  TResponsiveTableColumn,
  TResponsiveTableContextProps,
  TResponsiveTableRow,
  TResponsiveTableRowsSelectionEvent,
  TResponsiveTableSortChangeEvent,
} from '@apia/table';
import {
  arrayOrArray,
  getLabel,
  Mutex,
  TApiaFilter,
  TApiaFunction,
  TApiaFunctionPageInfo,
  uniqueId,
} from '@apia/util';
import { TFilter } from './filters/types';
import ApiaApi, { makeApiaUrl } from '../apiaApi';
import { Filter } from './filters/Filter';
import {
  QueryLoadStructureResponse,
  TApiaFiltersConditionsDefinition,
  TFilterTypesElementConditions,
} from './types';
import { TableController } from './table/TableController';
import { computed, makeObservable, observable, reaction } from 'mobx';
import { TableControllerEvents } from './table/types';
import { ApiaUtil } from '@apia/components';

export type QueryControllerStructure = {
  description: string;
  hasAdditionalInformation: boolean;
  isDynamic: boolean;
  openFiltersAutomatically: boolean;
  runFiltersAutomatically: boolean;
  hideFilterTypes: boolean;
  title: string;
  columns: TResponsiveTableColumn[];
  filters: TFilter[];
  queryData?: {
    noExecFirstTime?: boolean;
    autoFilter?: boolean;
    showAllAttributes?: boolean;
    showColumn?: string;
    name?: string;
    hideCount?: boolean;
    description?: string;
    storeColumn?: string;
    title?: string;
  };
};

export type StoredQueryControllerStructure = Omit<
  QueryControllerStructure,
  'columns' | 'filters'
>;

function unescapeHtml(html: string): string {
  const tmp: HTMLTextAreaElement = document.createElement('textarea');
  tmp.innerHTML = html;
  return tmp.value;
}

type QueryFocusedFilter = {
  filterId: string;
  filterPart?: string;
  filterScope?: string;
  selectionEnd: number | null;
  selectionStart: number | null;
} | null;

export class QueryController {
  protected hasInited = false;
  protected initializing = false;
  protected mutex: Mutex = new Mutex();
  protected filterRefreshDebounceMs = 650;
  protected pendingFocusedFilter: QueryFocusedFilter = null;

  public structure: StoredQueryControllerStructure | null = null;
  public tableController: TableController;
  conditions: TFilterTypesElementConditions[] = [];

  constructor(public ajaxUrl: string) {
    this.tableController = this.buildTableController();
    this.initEvents();

    makeObservable(this, { conditions: observable, isLoading: computed });

    reaction(
      () => this.mutex.isBusy,
      () => (this.tableController.state.isLoading = this.mutex.isBusy),
    );
  }

  protected async actualRefreshMethod(serverFirstRefresh?: boolean) {
    const focusedFilter = this.pendingFocusedFilter ?? this.getFocusedFilter();
    this.pendingFocusedFilter = null;
    const newValues = this.getFiltersValuesMap();
    const required = this.areRequiredFilters();
    if (
      !required ||
      (serverFirstRefresh && this.structure?.queryData?.noExecFirstTime)
    ) {
      await this.mutex.acquire();

      try {
        this.tableController.state.finishedFirstLoad = false;
        const refresh = await ApiaApi.post<TApiaFunction>(
          makeApiaUrl(this.getRefreshParameters()),
          {
            postData: newValues,
            postDataTreatement: 'stringify',
            stringifyOptions: { arrayFormat: 'repeat' },
          },
        );

        this.parseRefreshResponse(refresh?.data);
      } finally {
        if (serverFirstRefresh) {
          this.tableController.state.finishedFirstLoad = false;
        } else {
          this.tableController.state.finishedFirstLoad = true;
        }
        this.mutex.release();
        this.restoreFocusedFilter(focusedFilter);
      }
    }
    if (
      required &&
      !(serverFirstRefresh && this.structure?.queryData?.noExecFirstTime)
    ) {
      this.tableController.clearResults(false);

      const names = required
        .map(
          (f) =>
            f.filterState.title ??
            f.filterState.column ??
            f.filterState.id ??
            '',
        )
        .filter(Boolean)
        .join(', ');
      ApiaUtil.instance.notifications.notify({
        message: getLabel('msgQryFilterRequiredList', {
          text: { TOK1: names },
        }).text,
        type: 'warning',
      });
    }
  }

  protected areRequiredFilters() {
    const requiredFilters = [
      ...this.tableController.state.filters.values(),
    ].filter(
      (c) =>
        c.filterState.required &&
        !c.filterState.value &&
        !c.filterState.isHidden,
    );
    if (requiredFilters.length > 0) {
      return requiredFilters;
    }
    return false;
  }

  public hasRequiredColumnFilters(): boolean {
    const { columns, filters } = this.tableController.state;

    return [...filters.values()].some(({ filterState }) => {
      const column = columns.get(filterState.column ?? '');

      return (
        filterState.required &&
        !filterState.isAdditional &&
        !filterState.isHidden &&
        column !== undefined &&
        !column.hidden
      );
    });
  }

  public hasColumnFiltersWithDefaultValue(): boolean {
    const { columns, filters } = this.tableController.state;

    return [...filters.values()].some(({ filterState }) => {
      const column = columns.get(filterState.column ?? '');
      const hasDefaultValue =
        filterState.defaultValue !== undefined &&
        filterState.defaultValue !== null &&
        filterState.defaultValue !== '';

      return (
        hasDefaultValue &&
        !filterState.isAdditional &&
        !filterState.isHidden &&
        column !== undefined &&
        !column.hidden
      );
    });
  }

  protected buildTableController(): TableController {
    return new TableController();
  }

  public async clearFilters() {
    this.tableController.clearSelection();
    this.tableController.state.filters.forEach((c) => {
      c.filterState.value = '';
      c.filterState.filterToValue = '';
    });
    await this.actualRefreshMethod();
  }

  private timeout: any = -1;
  protected executeRefresh() {
    clearTimeout(this.timeout);
    this.pendingFocusedFilter = this.getFocusedFilter();

    this.timeout = setTimeout(() => {
      this.actualRefreshMethod();
    }, this.filterRefreshDebounceMs);
  }

  protected getFocusedFilter() {
    const activeElement = document.activeElement as HTMLElement | null;
    const filterElement = activeElement?.closest?.(
      '[data-query-filter-id]',
    ) as HTMLElement | null;
    const filterScope = activeElement?.closest?.(
      '[data-query-filter-scope]',
    ) as HTMLElement | null;

    if (!filterElement) return null;

    const filterId = filterElement.dataset.queryFilterId;
    if (!filterId) return null;

    return {
      filterId,
      filterPart: filterElement.dataset.queryFilterPart,
      filterScope: filterScope?.dataset.queryFilterScope,
      selectionEnd:
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement
          ? activeElement.selectionEnd
          : null,
      selectionStart:
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement
          ? activeElement.selectionStart
          : null,
    };
  }

  protected restoreFocusedFilter(focusedFilter: QueryFocusedFilter) {
    if (!focusedFilter) return;

    const restore = (attempt = 0) => {
      const activeElement = document.activeElement as HTMLElement | null;
      const activeFilter = activeElement?.closest?.(
        '[data-query-filter-id]',
      ) as HTMLElement | null;
      const activeInteractiveElement = activeElement?.closest?.(
        'input, textarea, select, button, a[href], [contenteditable="true"]',
      );

      if (
        activeFilter &&
        activeFilter.dataset.queryFilterId !== focusedFilter.filterId
      ) {
        return;
      }

      if (!activeFilter && activeInteractiveElement) {
        return;
      }

      const scope = focusedFilter.filterScope
        ? document.querySelector<HTMLElement>(
            `[data-query-filter-scope="${focusedFilter.filterScope}"]`,
          )
        : document;
      const candidates = [
        ...(scope ?? document).querySelectorAll<HTMLElement>(
          '[data-query-filter-id]',
        ),
      ];
      const target = candidates.find(
        (current) =>
          current.dataset.queryFilterId === focusedFilter.filterId &&
          (!focusedFilter.filterPart ||
            current.dataset.queryFilterPart === focusedFilter.filterPart),
      );
      const focusTarget = target?.matches(
        'input, textarea, select, button, [tabindex]',
      )
        ? target
        : target?.querySelector<HTMLElement>(
            'input, textarea, select, button, [tabindex]',
          );

      if (!focusTarget) {
        if (attempt < 5) {
          window.setTimeout(() => restore(attempt + 1), 50);
        }
        return;
      }

      focusTarget.focus();

      if (
        focusTarget instanceof HTMLInputElement ||
        focusTarget instanceof HTMLTextAreaElement
      ) {
        const { selectionStart, selectionEnd } = focusedFilter;
        if (selectionStart !== null && selectionEnd !== null) {
          focusTarget.setSelectionRange(selectionStart, selectionEnd);
        }
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => restore());
    });
  }

  public getTableControllerDefaultProps(): Omit<
    TResponsiveTableContextProps,
    | 'FiltersRenderer'
    | 'SelectionHandler'
    | 'filters'
    | 'columns'
    | 'rows'
    | keyof TableControllerEvents
  > &
    Pick<TResponsiveTableContextProps, 'statesColumns'> {
    return { statesColumns: [] };
  }

  protected getEventsHandlers() {
    return {
      onChangeSelection: (ev: TResponsiveTableRowsSelectionEvent) => {
        ev;
      },
      onFilterBlur: (_ev: TApiaFilter) => {},
      onFilterChange: (_ev: TApiaFilter) => {
        const filter = this.tableController.state.filters.get(_ev.id);
        if (filter?.filterState.runAutomatically) {
          this.refresh(false);
        }
      },
      onFilterPressEnter: (_ev: TApiaFilter) => {
        this.refresh(true);
      },
      onRowClick: (_ev: TResponsiveTableRowsSelectionEvent[number]) => {},
      onSelectRows: (_ev: TResponsiveTableRowsSelectionEvent) => {},
      onSortChange: (ev: TResponsiveTableSortChangeEvent) => {
        const isDesc = ['Up', 'Desc', 'D'].includes(ev.sortValue ?? 'A');

        const col = this.tableController.state?.columns.get(ev.column.name);
        if (col) {
          col.currentSorting = isDesc ? 'D' : 'A';
          this.tableController.state.columns = new Map(
            this.tableController.state.columns,
          );
        }

        this.sort(String(ev.columnIndex), isDesc ? 'Des' : 'Asc');
      },
      onPage: this.page.bind(this),
      onRefresh: () => this.refresh(true),
    };
  }

  protected getLoadStructureParameters(): Record<string, unknown> {
    return {
      ajaxUrl: this.ajaxUrl,
      action: 'init',
      react: true,
      //Importante para forzar paginacion en querys de modal ya que no existe parametro aún
      forcePagination: true,
    };
  }

  protected getClearFiltersParameters(): Record<string, unknown> {
    return {
      ajaxUrl: this.ajaxUrl,
      action: 'clearFilter',
    };
  }

  protected getFiltersValuesMap(): Record<string, unknown> {
    return [...this.tableController.state.filters.values()].reduce<
      Record<string, unknown>
    >((result, filterWrapper) => {
      const { id, value, filterToId, filterToValue } =
        filterWrapper.filterState;
      result[id] = filterWrapper.filterState.multiplier
        ? value * filterWrapper.filterState.multiplier
        : value;

      if (filterToId !== undefined && filterToValue != undefined) {
        result[filterToId] = filterWrapper.filterState.multiplier
          ? Number(filterToValue) * filterWrapper.filterState.multiplier
          : filterToValue;
      }

      return result;
    }, {});
  }

  public get isLoading() {
    return this.mutex.isBusy;
  }

  protected getPageParameters(): Record<string, unknown> {
    return {
      ajaxUrl: this.ajaxUrl,
      action: 'page',
    };
  }

  protected getRefreshParameters(): Record<string, unknown> {
    return {
      ajaxUrl: this.ajaxUrl,
      action: 'refresh',
      react: true,
    };
  }

  protected getSortParameters(): Record<string, unknown> {
    return {
      ajaxUrl: this.ajaxUrl,
      action: 'sort',
      react: true,
      orderJustThisColumn: true,
    };
  }

  protected async getStructure() {
    const result = await ApiaApi.post(
      makeApiaUrl(this.getLoadStructureParameters()),
    );

    if (!result?.data) {
      throw new Error('Cannot load Input query structure');
    }

    return this.parseLoadStructureResponse(result.data);
  }

  protected initEvents() {
    const handlers = this.getEventsHandlers();

    Object.entries(handlers).forEach(([eventName, handler]) => {
      this.tableController.on(eventName as any, handler.bind(this) as any);
    });
  }

  public async initialLoad() {
    this.tableController.state.finishedFirstLoad = false;
    if (this.initializing && !this.hasInited) {
      throw new Error('Cannot initialize the query twice');
    }
    this.initializing = true;

    if (!this.hasInited) {
      const structure = await this.getStructure();
      this.structure = structure;
      this.parseStructure(structure);
      this.tableController.updateState({
        props: this.getTableControllerDefaultProps(),
      });

      this.hasInited = true;
      this.initializing = false;

      this.loadFilterTypes();
      this.tableController.markInitialFilters();
      if (!this.structure.queryData?.noExecFirstTime) this.refresh();
    }
  }

  protected async loadFilterTypes() {
    if (!this.structure?.hideFilterTypes) {
      const result = await ApiaApi.post<TApiaFiltersConditionsDefinition>(
        makeApiaUrl({
          ajaxUrl: 'apia.query.ModalAction.run?',
          action: 'filterOptions',
        }),
      );
      if (result?.data?.form?.elements?.element) {
        this.conditions = arrayOrArray(result.data.form.elements.element);
      }
    }
  }

  public async page(num: number) {
    await this.mutex.acquire();

    try {
      const page = await ApiaApi.post<TApiaFunction>(
        makeApiaUrl({ ...this.getPageParameters(), pageNumber: num }),
      );

      this.parseRefreshResponse(page?.data);
    } finally {
      this.mutex.release();
    }
  }

  protected parseLoadStructureResponse(
    response: any,
  ): QueryControllerStructure & {
    columns: TResponsiveTableColumn[];
    filters: TFilter[];
  } {
    const typedResponse = response as QueryLoadStructureResponse;

    const retrievedColumns = arrayOrArray(typedResponse.columns.column);

    return {
      columns: retrievedColumns.map((c) => {
        const column: TResponsiveTableColumn = {
          label: c.title,
          name: c.name,
          additionalData: c,
          allowSorting: c.enableOrder !== false,
          ariaLabel: c.title,
          currentSorting: c.order as 'D',
          hidden: c.hide === true || c.hide == '1',
          hideFromAccordion: c.hide === true,
          id: c.name,
          showAsAdditional: c.asMoreInformation,
          title: c.toolTip,
          width: c.width === '' || c.width === undefined ? '300px' : c.width,
        };

        return column;
      }),
      description: typedResponse.queryData?.description,
      filters: arrayOrArray(typedResponse.filters.filter)
        .filter((f) => !!f.column)
        .map((f, i) => {
          const filter: TFilter = {
            id: f.id ?? `filter${String(i + 1)}`, // Id generado en APIA
            filterToId:
              (f?.filterToId ?? f.type == 'D')
                ? `filter${String(i + 1)}i`
                : undefined, // Id generado en APIA
            defaultValue: f.defaultValue,
            value: f.currentValue,
            avoidLabel:
              f.type === 'D' ||
              f.type === 'date' ||
              f.type === 'S' ||
              arrayOrArray(f?.filterOption).length > 0 ||
              f.type === 'N'
                ? true
                : false,
            filterToValue: f?.filterToValue,
            column: f.column,
            isAdditional: f.asAdditional,
            isHidden: f.hideFilterOption,
            title: f.title,
            tooltip: f.toolTip,
            type: f.type as 'S',
            hideToFilter: true,
            onChange: () => {},
            options: f?.filterOption
              ? [
                  { value: '', label: '' },
                  ...arrayOrArray(f?.filterOption).map((c) => ({
                    label: c.content,
                    value: c.value,
                  })),
                ]
              : undefined,
            placeholder: f.title,
            readonly: f.readonly,
            required:
              f.required === 'true' ||
              f.required === '1' ||
              f.dateRequired === '1',
            showAsInteger: f.showAsInteger,
            runAutomatically:
              typedResponse?.filters.runFiltersAutomatically ||
              f.detectOnChange,
          };

          return filter;
        }),
      hasAdditionalInformation: !!retrievedColumns.find(
        (c) => c.asMoreInformation,
      ),
      isDynamic: false,
      title: typedResponse.queryData?.title,
      openFiltersAutomatically: typedResponse.filters.openFiltersAutomatically,
      runFiltersAutomatically: typedResponse.filters.runFiltersAutomatically,
      hideFilterTypes: typedResponse.filters.hideFilterTypes,
      queryData: typedResponse?.queryData,
    };
  }

  protected parseRefreshResponse(response?: TApiaFunction | null) {
    const columns = [...this.tableController.state.columns.values()];
    const rows = response?.function?.messages?.result?.table?.row;
    const pageState = response?.function.messages.result.pageInfo;

    this.updatePageState(pageState);
    this.updateRows([
      ...arrayOrArray(rows).map((r) => {
        return {
          forbidSelection: r.unselectableTR,
          initiallySelected: r.selected,
          isLocked: r.isLocked,
          rowProps: r,
          cells: (() => {
            const hasAdditionalCol = columns.some((c) => c.showAsAdditional);

            const rawCells = arrayOrArray(r.cell);

            const isAdditionalCell = (x: any) =>
              x &&
              typeof x === 'object' &&
              !Array.isArray(x) &&
              'moreInfo' in x;
            const normalCells = rawCells.filter((c) => !isAdditionalCell(c));
            const additionalCells = rawCells.filter((c) =>
              isAdditionalCell(c),
            ) as any as [{ moreInfo: string }];

            return [
              ...columns.filter((c) => c.hidden).map(() => ({})),

              ...normalCells.map((c) => ({
                children: typeof c === 'string' ? unescapeHtml(c) : c,
              })),

              ...(hasAdditionalCol
                ? additionalCells.map((a) => ({
                    children: a.moreInfo,
                    className: '',
                    id: uniqueId(),
                    title: uniqueId(),
                  }))
                : []),
            ].filter(Boolean);
          })(),
          className: r.classToAdd,
          id: r.id ?? uniqueId('row'),
        } as TResponsiveTableRow;
      }),
    ]);
  }

  protected parseStructure(
    structure: ReturnType<QueryController['parseLoadStructureResponse']>,
  ) {
    const columns = new Map(
      arrayOrArray(structure.columns).map((c) => [c.name, c]),
    );
    const filters = new Map(
      arrayOrArray(structure.filters).map((f) => {
        return [f.id, new Filter(f)];
      }),
    );

    if (columns.size) {
      this.tableController.updateState({
        columns,
      });
    }
    if (filters.size) {
      this.tableController.updateState({
        filters,
      });
    }
  }

  public async refresh(now: boolean = true) {
    this.pendingFocusedFilter = this.getFocusedFilter();
    if (now) {
      clearTimeout(this.timeout);
    }
    return now ? this.actualRefreshMethod() : this.executeRefresh();
  }

  public async setFilterOptions() {
    await ApiaApi.post<TApiaFiltersConditionsDefinition>(
      makeApiaUrl({
        ajaxUrl: this.ajaxUrl,
        action: 'setFilterOptions',
      }),
      {
        postData: this.conditions.reduce<Record<string, string>>((acc, c) => {
          acc[c.id] = c.value;
          return acc;
        }, {}),
        postDataTreatement: 'stringify',
      },
    );
  }

  public async sort(column: string, orderType: 'Asc' | 'Des') {
    await this.mutex.acquire();
    try {
      const result = await ApiaApi.post<TApiaFunction>(
        makeApiaUrl({
          ...this.getSortParameters(),
          orderBy: column,
          orderType,
        }),
      );
      if (result?.data) {
        const structure = this.parseLoadStructureResponse(result.data);
        this.parseStructure(structure);
        this.parseRefreshResponse(result.data);
      }
    } finally {
      this.mutex.release();
    }
  }

  protected updatePageState(pageInfo?: TApiaFunctionPageInfo) {
    const currentPage = Number(pageInfo?.currentPage ?? 0);
    const totalPages = Number(pageInfo?.pageCount ?? 0);
    const totalRecords = Number(pageInfo?.totalRecords ?? 0);

    this.tableController.updateState({
      pagination: { currentPage, totalPages, totalRecords },
    });
  }

  protected updateRows(rows: TResponsiveTableRow[]) {
    this.tableController.state.rows = rows;
  }
}
